'use client';

import { useTranslations } from 'next-intl';

import MissedIcon from '@/assets/icons/account/missedIcon.svg';
import WinIcon from '@/assets/icons/account/winIcon.svg';
import UsdSvg from '@/assets/icons/usd.svg';
import { HoverPopover } from '@/components/shared/HoverPopover';
import { P } from '@/components/shared/Typography';
import { cn } from '@/lib/utils/cn';
import { formatTimestampToGMTString } from '@/lib/utils/formatTimestampToGMTString';
import { C_POOL_TOKEN_DECIMALS } from '@/ts/constants/C_DECIMAL_PLACES';

export type T_PoolsHistoryCard = {
  type: 'buy' | 'sell';
  boughtAt: string;
  tradingPair: string;
  token: string;
  tokens: string;
  totalCost: string;
  marketValue: string;
  profit: number;
  blockTimestamp: string;
};

function PoolsHistoryCard({
  type,
  boughtAt,
  tradingPair,
  token,
  tokens,
  totalCost,
  marketValue,
  profit,
  blockTimestamp
}: T_PoolsHistoryCard) {
  const t = useTranslations();

  const isBuy = type === 'buy';
  const isProfit = profit > 0;
  const profitText = profit > 0 ? `+${profit.toFixed(2)}` : profit.toFixed(2);
  const profitTextWith6Decimals =
    profit > 0
      ? `+${profit.toFixed(C_POOL_TOKEN_DECIMALS)}`
      : profit.toFixed(C_POOL_TOKEN_DECIMALS);

  return (
    <div className="flex flex-col bg-accordion-brand rounded-xl ">
      <div className="grid grid-cols-3 gap-4 p-4 bg-card rounded-tl-xl rounded-tr-xl ">
        <div className="flex flex-row justify-start items-center gap-2.5">
          <span className="bg-secondary rounded-xl">
            {isBuy ? <WinIcon /> : <MissedIcon />}
          </span>
          <div className="flex flex-col items-start justify-center">
            <P className="text-muted-foreground text-xs font-light mb-0.5">
              {isBuy
                ? t('profile.poolsHistory.boughtAt')
                : t('profile.poolsHistory.soldAt')}
            </P>
            <P icon={<UsdSvg />} className="text-sm font-bold">
              {boughtAt}
            </P>
          </div>
        </div>
        <div className="flex flex-col justify-center items-center">
          <P className="text-muted-foreground text-xs font-light mb-0.5">
            {t('profile.poolsHistory.tradingPair')}
          </P>
          <P className="text-sm font-bold">{tradingPair}</P>
        </div>
        <div className="flex flex-col justify-center items-center">
          <P className="text-muted-foreground text-xs font-light mb-0.5">
            {t('profile.poolsHistory.tokens')}
          </P>
          <P className="text-sm font-bold">{tokens}</P>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 p-4 rounded-tl-xl rounded-tr-xl ">
        <div className="flex flex-row justify-center items-center gap-2.5">
          {isBuy && (
            <div className="flex flex-col items-start justify-center">
              <P className="text-muted-foreground text-xs font-light mb-0.5">
                {t('profile.poolsHistory.totalCost')}
              </P>
              <P icon={<UsdSvg />} className="text-sm font-bold">
                {totalCost}
              </P>
            </div>
          )}
        </div>
        <div className="flex flex-col justify-center items-center">
          <P className="text-muted-foreground text-xs font-light mb-0.5">
            {t('profile.poolsHistory.marketValue')}
          </P>
          <P icon={<UsdSvg />} className="text-sm font-bold">
            {marketValue}
          </P>
        </div>
        {isBuy && (
          <div className="flex flex-col justify-center items-center">
            <P className="text-muted-foreground text-xs font-light mb-0.5">
              {t('profile.poolsHistory.profit')}
            </P>
            <HoverPopover
              trigger={
                <P
                  className={cn(
                    'text-sm font-bold cursor-help',
                    isProfit ? 'text-primary' : 'text-muted-foreground'
                  )}
                >
                  {profitText} %
                </P>
              }
              side="top"
              align="center"
              sideOffset={8}
            >
              <P className="text-xs font-semibold leading-none text-popover-foreground">
                {profitTextWith6Decimals} %
              </P>
            </HoverPopover>
          </div>
        )}
      </div>
      <div className="flex border-1 w-full " />
      <P className="flex w-full justify-center text-muted-foreground text-xs font-light py-4">
        {formatTimestampToGMTString(blockTimestamp)}
      </P>
    </div>
  );
}

export default PoolsHistoryCard;
